import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, inject, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { HotToastService } from '@ngxpert/hot-toast';
import { DialogService } from '@ngneat/dialog';

import { isMobileView } from '@core/utils';
import { ConfirmationDialogType, DeleteConfirmationOptions, NotificationType, SectionType } from '@core/enums';
import { ImageConversionService } from '@core/services/image-conversion.service';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs/m-delete-confirmation-dialog/m-delete-confirmation-dialog.component';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs/confirmation-dialog/confirmation-dialog.component';

@UntilDestroy()
@Component({
  selector: 'app-image-selector',
  imports: [CommonModule, ButtonComponent, IconComponent, TranslateModule, ImageCropperComponent],
  templateUrl: './image-selector.component.html',
  styleUrls: ['./image-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageSelectorComponent),
      multi: true,
    },
  ],
})
/**
 * Image Selector Component
 * @description A component that allows the user to select an image or document from the file system or by dragging and dropping.
 * The component also allows the user to crop the image before emitting the selected image.
 *
 * @property {boolean} fullWidthPreview - If true, the image preview will take up the full width of the container. Default is false.
 * @property {boolean} imageCropper - If true, the image will be cropped before emitting the selected image. Default is false.
 * @property {number} aspectRatio - The aspect ratio of the image to be cropped. Default is 1.
 * @property {boolean} emitBase64 - If true, the selected image will be emitted as a base64 string. Otherwise, it will be emitted as a File object. Default is false.
 * @property {boolean} allowDocument - If true, the user can select documents in addition to images. Default is false.
 * @property {boolean} isRoundedPreview - If true, the image preview will be round. Default is false.
 *
 * @example
 * <app-image-selector
 *  [fullWidthPreview]="true"
 *  [imageCropper]="true"
 *  [aspectRatio]="1"
 *  [emitBase64]="true"
 *  [allowDocument]="true"
 *  [isRoundedPreview]="true"
 *  (imageSelected)="onImageSelected($event)"
 *  (fileSelected)="onFileSelected($event)">
 *  </app-image-selector>
 *
 */
export class ImageSelectorComponent implements OnInit, ControlValueAccessor {
  /* Below Output properties are defining an `EventEmitter` named `imageSelected` and `fileSelected` with a generic type of `string
  | File`. This `EventEmitter` can emit values of type `string` or `File`. Used for
  emitting events when an image or file is selected. */
  @Output() imageSelected: EventEmitter<string | File> = new EventEmitter<string | File>();
  @Output() fileSelected: EventEmitter<string | File> = new EventEmitter<string | File>();

  @Output() deleteImage: EventEmitter<void> = new EventEmitter<void>();

  /**
   * The `fullWidthPreview` property is a boolean value that determines whether the image preview should take up the full width of the container.
   */
  @Input() fullWidthPreview = false;
  /**
   * The `imageCropper` property is a boolean value that determines whether the image should be cropped before emitting the selected image.
   */
  @Input() imageCropper = false;
  /**
   * The `aspectRatio` property is a number that represents the aspect ratio of the image to be cropped.
   */
  @Input() aspectRatio = 1;
  /**
   * The `emitBase64` property is a boolean value that determines whether the selected image should be emitted as a base64 string.
   */
  @Input() emitBase64 = false;
  /**
   * The `allowDocument` property is a boolean value that determines whether the user can select documents in addition to images.
   */
  @Input() allowDocument = false;
  /**
   * The `isRoundPreview` property is a boolean value that determines whether the image preview should be round.
   */
  @Input() isRoundedPreview = false;
  /**
   * The `sizeLimit` property is a number that represents the maximum file size allowed in bytes.
   * The default value is 10485760 bytes, which is equivalent to 10 MB.
   * 5242880 for 5 MB
   */
  @Input() sizeLimit = 10485760; // 10 MB

  @Input() format: 'png' | 'jpeg' | 'webp' = 'webp';

  /**
   * The `hideClearButton` property is a boolean value that determines whether the clear button should be hidden.
   */
  @Input() hideClearButton = false;

  @Input() isUpdateMode = false;

  @Input() entityId: number;

  @Input() sectionType: SectionType;
  croppedImage: any = '';
  fileChangeEvent: Event = null;
  cropperVisible = false;
  isDocument = false;
  imageWidth: number;
  imageHeight: number;
  cropperPosition: { x1: number; y1: number; x2: number; y2: number } = {
    x1: 0,
    y1: 0,
    x2: 200,
    y2: 200,
  };
  protected _selectedFileName = 'image';
  protected _selectedImageUrl = '';
  private _originalImageUrl = '';
  private readonly _toastService: HotToastService = inject(HotToastService);
  private readonly _translateService: TranslateService = inject(TranslateService);
  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _imageConversionService: ImageConversionService = inject(ImageConversionService);
  private readonly _bottomSheetService: NgxBottomSheetService = inject(NgxBottomSheetService);
  private readonly _renderer: Renderer2 = inject(Renderer2);
  private readonly _elementRef: ElementRef = inject(ElementRef);
  private readonly _dialogService: DialogService = inject(DialogService);

  get selectedImageUrl(): string {
    return this._selectedImageUrl;
  }

  @Input() set selectedImageUrl(value: string) {
    if (this.isUpdateMode && !this._originalImageUrl && value) {
      this._originalImageUrl = value;
    }
    this._selectedImageUrl = value;
    this.onChange(value);
  }

  get willOnlyClearImage(): boolean {
    return this._selectedImageUrl !== this._originalImageUrl;
  }

  onChange = (_: any) => {
    // this function is supposed to empty or to be available in future
  };
  onTouched = () => {
    // this function is supposed to empty or to be available in future
  };

  ngOnInit() {
    this._setupDragAndDrop();
  }

  /**
   * The `cropperReady` function calculates the dimensions of the cropper based on the aspect ratio and
   * sets the cropper position accordingly.
   * @description The `cropperReady` function calculates the dimensions of the cropper based on the aspect
   * ratio of the image and sets the cropper position accordingly. The function first checks if the aspect
   * ratio of the image is greater than the specified aspect ratio. If it is, the function calculates the
   * width and height of the cropper based on the aspect ratio and sets the cropper position accordingly.
   */
  cropperReady() {
    let cropperWidth, cropperHeight;

    const aspectRatio = this.imageWidth / this.imageHeight;
    if (aspectRatio > this.aspectRatio) {
      if (aspectRatio > 3) {
        cropperHeight = Math.min(this.imageHeight, 80);
        cropperWidth = cropperHeight * this.aspectRatio;
      } else if (aspectRatio > 2) {
        cropperHeight = Math.min(this.imageHeight, 100);
        cropperWidth = cropperHeight * this.aspectRatio;
      } else {
        cropperHeight = Math.min(this.imageHeight, 200);
        cropperWidth = cropperHeight * this.aspectRatio;
      }

      this.cropperPosition = {
        x1: 0,
        y1: 0,
        x2: cropperWidth,
        y2: cropperHeight,
      };
    } else if (aspectRatio < 1 && aspectRatio > 0) {
      cropperWidth = Math.min(this.imageWidth, 200);
      cropperHeight = cropperWidth / this.aspectRatio;
      this.cropperPosition = {
        x1: 0,
        y1: 0,
        x2: cropperWidth,
        y2: cropperHeight,
      };
    } else {
      this.cropperPosition = null;
    }

    this._changeDetectorRef.detectChanges();
  }

  /**
   * The `handleFileDrop` function checks if files were dropped, retrieves the first file, and then
   * validates and processes it.
   * @param {FileList} files - The `handleFileDrop` function takes a `FileList` object as a parameter,
   * which represents a list of files that have been dropped into a drop zone or selected using a file
   * input. The function checks if there is at least one file in the list, retrieves the first file from
   * the list
   */
  handleFileDrop(files: FileList) {
    if (files.length > 0) {
      const file = files[0];
      this._validateAndProcessFile(file);
    }
  }

  /**
   * The function `isWithinFileSizeLimit` checks if a file size is within the limit of 10 MB.
   * @param {number} fileSize - The `fileSize` parameter represents the size of a file in bytes. The
   * `isWithinFileSizeLimit` function checks if the file size is within a specified limit, which is 10 MB
   * (10485760 bytes).
   * @returns The `isWithinFileSizeLimit` function is returning a boolean value indicating whether the
   * `fileSize` is less than or equal to 10485760 bytes (10 MB).
   */

  isWithinFileSizeLimit(fileSize: number): boolean {
    return fileSize <= 10485760; // 10 MB
  }

  /**
   * The `openImageBrowser` function creates an input element for selecting files, sets file type
   * restrictions based on a condition, listens for file selection changes, and triggers file validation
   * and processing.
   */
  openImageBrowser() {
    const fileInput = this._renderer.createElement('input');
    this._renderer.setAttribute(fileInput, 'type', 'file');
    this._renderer.setAttribute(
      fileInput,
      'accept',
      this.allowDocument ? 'image/png, image/jpeg, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'image/png, image/jpeg',
    );
    this._renderer.setStyle(fileInput, 'display', 'none');

    // Append the input to the body as a workaround for the file event not passed to the input element in chrome and safari because the element is not attached to the DOM
    this._renderer.appendChild(this._elementRef.nativeElement, fileInput);

    this._renderer.listen(fileInput, 'change', (event: Event) => {
      const inputFileElement = event.target as HTMLInputElement;

      if (inputFileElement && inputFileElement.files && inputFileElement.files.length > 0) {
        const file = inputFileElement.files[0];
        this.cropperPosition = null;
        this.fileChangeEvent = event;
        this._validateAndProcessFile(file);
      }
      this._renderer.removeChild(this._elementRef.nativeElement, fileInput);
    });

    fileInput.click();
  }

  /**
   * The `imageCropped` function handles the cropping of an image and emits the cropped image in either
   * base64 format or as a file based on a condition.
   * @param {ImageCroppedEvent} event - The `imageCropped` function takes an `ImageCroppedEvent` object
   * as a parameter named `event`. This object likely contains information about the cropped image, such
   * as the base64 representation of the image data and the selected file name. The function then
   * processes this information based on the value of the `emitBase64` property.
   */
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
    this._selectedImageUrl = event.base64;
    if (this.emitBase64) {
      this.imageSelected.emit(this.croppedImage);
      this.fileSelected.emit(this.croppedImage);
      this.onChange(this.croppedImage);
    } else {
      this._imageConversionService
        .convertBase64ToFile(event.base64, this._selectedFileName)
        .then((file) => {
          this.imageSelected.emit(file);
          this.fileSelected.emit(file);
          this.onChange(file);
        })
        .catch((error) => console.error('Error converting image', error));
    }
  }

  writeValue(obj: any): void {
    if (obj) {
      this._selectedImageUrl = obj;
      this._originalImageUrl = obj;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  clearImage() {
    if (this.isUpdateMode && this.entityId && this.sectionType) {
      if (this._originalImageUrl && this._selectedImageUrl !== this._originalImageUrl) {
        this._setImageUrl(this._originalImageUrl);
        this.onChange(this._originalImageUrl);
        this.fileSelected.emit(null);
        this.imageSelected.emit(null);
        this.croppedImage = '';
        this.fileChangeEvent = null;
        this.cropperVisible = false;
        this.isDocument = false;
        return;
      }

      if (isMobileView()) {
        const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
          ...DefaultDeleteDialogConfig,
          data: {
            text: this._translateService.instant('Image'),
          },
        });
        confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
          next: (res) => {
            switch (res) {
              case DeleteConfirmationOptions.DELETE:
                if (res) {
                  this._deleteImage();
                }
                break;
              default:
                break;
            }
          },
        });
      } else {
        this._dialogService
          .open(ConfirmationDialogComponent, {
            size: 'md',
            closeButton: false,
            windowClass: 'dialog-overflow-unset',
            data: {
              type: ConfirmationDialogType.DELETE_IMAGE,
            },
          })
          .afterClosed$.pipe(untilDestroyed(this))
          .subscribe({
            next: (confirmed: boolean) => {
              if (confirmed) {
                this._deleteImage();
              }
            },
          });
      }
    } else {
      // In create mode: simply clear the image
      this._clearImageState();
    }
  }

  private _clearImageState() {
    this._selectedImageUrl = '';
    this.croppedImage = '';
    this.fileChangeEvent = null;
    this.cropperVisible = false;
    this.isDocument = false;
    this.onChange(null);
    this.fileSelected.emit(null);
    this.imageSelected.emit(null);
    this.deleteImage.emit();
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  /**
   * The `setupDragAndDrop` function sets up drag and drop functionality for a specified element.
   */
  private _setupDragAndDrop() {
    const dropArea = this._elementRef.nativeElement.querySelector('.image-selection-container');
    this._renderer.listen(dropArea, 'dragover', (event) => {
      event.preventDefault();
    });
    this._renderer.listen(dropArea, 'drop', (event: DragEvent) => {
      event.preventDefault();
      this.handleFileDrop(event.dataTransfer.files);
    });
  }

  /**
   * The function `validateAndProcessFile` checks if the file type is valid and processes the file
   * accordingly in a TypeScript class.
   * @param {File} file - The `validateAndProcessFile` function takes a `File` object as a parameter.
   * This `File` object likely represents a file that the user has selected for processing.
   */
  private _validateAndProcessFile(file: File) {
    if ((this._isValidFileType(file.type) || (this.allowDocument && this._isValidDocumentType(file.type))) && file.size <= this.sizeLimit) {
      this.isDocument = !this._isValidFileType(file.type);
      this._selectedFileName = file.name;
      this._readFile(file);
    } else {
      // alert('Invalid file type or size. Only PNG, JPEG images and allowed documents under 10MB are allowed');
      this._toastService.error(this._translateService.instant('Invalid file type or size. Only PNG, JPEG images and allowed documents under 10MB are allowed'));
      this._resetComponentStateOnInvalidFile();
    }
  }

  /**
   * The function `isValidFileType` checks if the given file type is either 'image/png' or 'image/jpeg'
   * and returns a boolean value.
   * @param {string} fileType - The `fileType` parameter in the `isValidFileType` function is a string
   * that represents the type of a file. The function checks if the `fileType` is either 'image/png' or
   * 'image/jpeg' and returns a boolean value indicating whether the file type is valid or not.
   * @returns A boolean value is being returned, indicating whether the fileType is either 'image/png' or
   * 'image/jpeg'.
   */
  private _isValidFileType(fileType: string): boolean {
    return fileType === 'image/png' || fileType === 'image/jpeg';
  }

  /**
   * The function `isValidDocumentType` checks if a given file type is a valid document type.
   * @param {string} fileType - The `isValidDocumentType` function takes a `fileType` parameter, which is
   * a string representing the type of a document file. The function checks if the `fileType` is one of
   * the valid document types: PDF, MS Word, or Word document in Open XML format. It returns a
   * @returns The function `isValidDocumentType` returns a boolean value indicating whether the input
   * `fileType` is a valid document type.
   */
  private _isValidDocumentType(fileType: string): boolean {
    return ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(fileType);
  }

  /**
   * The `readFile` function reads a file and handles it based on whether it is a document or an image.
   * @param {File} file - The `file` parameter in the `readFile` function represents the file that is
   * being read by the FileReader. This file could be an image, a document, or any other type of file
   * that the function is designed to handle. The function checks if the file is a document, in which
   * case it emits the file and sets the selected image URL to a default document image. If the file is
   * an image, the function reads the file as a data URL, sets the selected image URL to the data URL,
   * and checks if the image cropper is enabled. If the image cropper is enabled, the function sets the
   * cropper visibility to true and triggers change detection. If the image cropper is not enabled, the
   * function emits the image or file based on the `emitBase64` property.
   */
  private _readFile(file: File) {
    if (this.isDocument) {
      this._selectedImageUrl = 'assets/image/document-file.png';
      this.cropperVisible = false;
      this.fileSelected.emit(file);
      this.imageSelected.emit(file);
      this.onChange(file);
      this._changeDetectorRef.detectChanges();
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result as string;
        const image = new Image();
        image.src = result;
        image.onload = () => {
          this.imageWidth = image.width;
          this.imageHeight = image.height;
          this._setImageUrl(result);
          if (this.imageCropper) {
            this.cropperVisible = true;
            this.cropperReady();
            this._changeDetectorRef.detectChanges();
          } else {
            this._emitImageOrFile(result, file);
          }
        };
      };
      reader.onerror = () => alert('Error reading file');
      reader.readAsDataURL(file);
    }
  }

  /**
   * The function `emitImageOrFile` emits either a base64 string or a File object based on a condition.
   * @param {string} base64 - Base64 is a binary-to-text encoding scheme that represents binary data in
   * an ASCII string format. It is commonly used to encode binary data, such as images, into a text
   * format that can be easily transmitted over text-based protocols like HTTP.
   * @param {File} file - The `file` parameter in the `emitImageOrFile` function is of type `File`. It
   * represents a file selected by the user, typically from an input element of type file in a form.
   */
  private _emitImageOrFile(base64: string, file: File) {
    if (this.emitBase64) {
      this.imageSelected.emit(base64);
      this.fileSelected.emit(base64);
      this.onChange(base64);
    } else {
      this.imageSelected.emit(file);
      this.fileSelected.emit(file);
      this.onChange(file);
    }
  }

  /**
   * The private method `_setImageUrl` sets the selected image URL and triggers change detection in
   * Angular.
   * @param {string} imageUrl - The `imageUrl` parameter in the `_setImageUrl` method is a string that
   * represents the URL of an image that you want to set as the selected image.
   */

  private _setImageUrl(imageUrl: string) {
    this._selectedImageUrl = imageUrl;
    this._changeDetectorRef.detectChanges();
  }

  /**
   * The function `_resetComponentStateOnInvalidFile` resets the state of certain component properties
   * to their default values.
   */
  private _resetComponentStateOnInvalidFile() {
    this.fileChangeEvent = null;
    this.croppedImage = '';
    this.isDocument = false;
    this._changeDetectorRef.detectChanges();
  }

  private _deleteImage() {
    this._imageConversionService
      .deleteEntityImage(this.sectionType.toLowerCase() as NotificationType, this.entityId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this._toastService.success(this._translateService.instant('Delete Successful'));
          this._clearImageState();
        },
        error: (error) => {
          this._toastService.error(error?.error?.message || this._translateService.instant('Something went wrong'));
          console.error('Deletion failed', error);
        },
      });
  }
}
